/**
 * SwimmingChampionshipsPage — Hub de Competições
 * Route: /natacao/campeonatos
 * Filter buttons: Todos | Brasil | Australia | Estados Unidos | Mundo | Europa | Outros
 * Design: VUKZ theme, sem emojis, botões seletores flat
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight } from 'lucide-react';
import { VUKZ } from '../theme/vukzTheme';
import { getCompetitionsHub } from '../services/swimmingService';
import { useSportsLang } from '../contexts/SportsLangContext';

const LABELS = {
  pt: {
    title: 'Competições',
    subtitle: 'Campeonatos de natação — Brasil, Austrália, Estados Unidos e internacionais',
    searchPlaceholder: 'Buscar competição...',
    filterAll: 'Todos',
    noResults: 'Nenhuma competição encontrada.',
    errorLoad: 'Erro ao carregar competições.',
  },
  en: {
    title: 'Competitions',
    subtitle: 'Swimming championships — Brazil, Australia, United States and international',
    searchPlaceholder: 'Search competition...',
    filterAll: 'All',
    noResults: 'No competitions found.',
    errorLoad: 'Error loading competitions.',
  }
};

// ─── Logo map ─────────────────────────────────────────────────────────────────
const LOGOS = {
  'Troféu José Finkel':               '/logo campeonatos natacao/josefinkel.jpg',
  'Troféu Maria Lenk':                '/logo campeonatos natacao/marialenk.jpg',
  'Campeonato Brasileiro':            '/logo campeonatos natacao/josefinkel.jpg',
  'Seletiva Australiana':             '/logo campeonatos natacao/australiantrials.png',
  'Campeonato Nacional dos EUA':      '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
  'TYR Pro Swim Series':              '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
  'Seletiva Olímpica dos EUA':        '/logo campeonatos natacao/toyota-us-open-championships-logo.png',
  'Olimpíadas':                       '/logo campeonatos natacao/paris2024.png',
  'Campeonato Mundial de Natação':    '/logo campeonatos natacao/wolrdchampionsdoha2024.png',
  'Copa do Mundo de Natação':         '/logo campeonatos natacao/wolrdchampionsdoha2024.png',
  'Campeonato Europeu de Natação':    '/logo campeonatos natacao/europeanaquatics.jpg',
  'Junior Pan Pacific Championships': '/logo campeonatos natacao/juniorpanpacs.png',
};

const REGION_ORDER = ['Brasil', 'Australia', 'Estados Unidos', 'Mundo', 'Europa', 'Outros'];
const REGION_LABELS = {
  Brasil:           'Brasil',
  Australia:        'Austrália',
  'Estados Unidos': 'Estados Unidos',
  Mundo:            'Mundo',
  Europa:           'Europa',
  Outros:           'Outros',
};

const normalizeCategory = (cat) => {
  const v = (cat || '').toLowerCase();
  if (['senior', 'sênior', 'absoluto', 'adulto'].includes(v)) return 'Absoluto';
  if (v === 'junior') return 'Junior';
  if (v.startsWith('infantil')) return 'Infantil';
  if (v.startsWith('juvenil')) return 'Juvenil';
  return cat || 'Absoluto';
};

// ─── Competition Card ─────────────────────────────────────────────────────────
const CompetitionCard = ({ league, onClick }) => {
  const logo = LOGOS[league.groupName];
  const regionLabel = REGION_LABELS[league.region] || league.region;

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-2xl border overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-pointer group"
      style={{ borderColor: VUKZ.border }}
    >
      <div className="h-1" style={{ background: `linear-gradient(to right, ${VUKZ.primary}, ${VUKZ.primaryDark})` }} />
      <div className="p-5">
        <div className="flex items-center gap-4">
          {/* Logo */}
          <div className="w-14 h-14 rounded-xl border flex items-center justify-center overflow-hidden flex-shrink-0 bg-gray-50"
            style={{ borderColor: VUKZ.border }}>
            {logo ? (
              <img src={logo} alt={league.groupName} className="w-11 h-11 object-contain"
                onError={(e) => {
                  e.target.parentElement.innerHTML =
                    `<div class="w-11 h-11 bg-gray-100 rounded-lg flex items-center justify-center"><span class="text-xl font-black text-gray-300">${(league.groupName || 'N')[0]}</span></div>`;
                }} />
            ) : (
              <div className="w-11 h-11 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-xl font-black text-gray-300">{(league.groupName || 'N')[0]}</span>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-extrabold text-gray-900 leading-tight group-hover:text-gray-700 transition-colors">
              {league.groupName}
            </h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {league.years && league.years.length > 0 && (
                <span className="text-sm font-semibold text-gray-400">
                  {league.years[0]}{league.years.length > 1 ? ` — ${league.years[league.years.length - 1]}` : ''}
                </span>
              )}
              <span className="text-xs font-semibold px-2 py-0.5 rounded-md"
                style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.primary }}>
                {regionLabel}
              </span>
            </div>
          </div>

          <ChevronRight size={20} className="text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
        </div>

        {/* Categories */}
        {league.categories && league.categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t" style={{ borderColor: '#f3f4f6' }}>
            {[...new Set(league.categories.map(normalizeCategory))].map((c, i) => (
              <span key={i} className="text-xs font-bold px-2.5 py-1 rounded-lg"
                style={{ backgroundColor: '#f3f4f6', color: VUKZ.textLight }}>
                {c}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Filter Button ────────────────────────────────────────────────────────────
const FilterBtn = ({ label, count, active, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold border transition-all duration-150 whitespace-nowrap"
    style={active
      ? { backgroundColor: VUKZ.primary, color: '#fff', borderColor: VUKZ.primary }
      : { backgroundColor: '#fff', color: VUKZ.text, borderColor: VUKZ.border }
    }
  >
    {label}
    <span className="text-xs font-extrabold px-1.5 py-0.5 rounded-md"
      style={active
        ? { backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }
        : { backgroundColor: VUKZ.primaryLight, color: VUKZ.primary }
      }>
      {count}
    </span>
  </button>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const SwimmingChampionshipsPage = () => {
  const navigate = useNavigate();
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [regionFilter, setRegionFilter] = useState('Todos');

  useEffect(() => {
    getCompetitionsHub()
      .then(hub => {
        setLeagues(hub.map(card => ({
          region:    card.region,
          groupName: card.groupName,
          years:     card.years || [],
          categories:card.categories || [],
          id:        card.id || card.competition_id,
          yearToId:  card.yearToId || {},
        })));
      })
      .catch(err => {
        console.error('[Competitions]', err);
        setError(t.errorLoad);
      })
      .finally(() => setLoading(false));
  }, []);

  // Deduplicate by groupName — merge years across duplicate API entries
  const deduped = useMemo(() => {
    const map = {};
    leagues.forEach(l => {
      const key = l.groupName;
      if (!map[key]) {
        map[key] = { ...l, years: [...l.years], categories: [...l.categories], yearToId: { ...l.yearToId } };
      } else {
        const merged = new Set([...map[key].years, ...l.years]);
        map[key].years = [...merged].sort((a, b) => b - a);
        map[key].yearToId = { ...map[key].yearToId, ...l.yearToId };
        const cats = new Set([...map[key].categories, ...l.categories]);
        map[key].categories = [...cats];
      }
    });
    return Object.values(map);
  }, [leagues]);

  // Count per region for filter buttons
  const regionCounts = useMemo(() => {
    const counts = { Todos: deduped.length };
    deduped.forEach(l => {
      const r = l.region || 'Outros';
      counts[r] = (counts[r] || 0) + 1;
    });
    return counts;
  }, [deduped]);

  // Only show regions that actually have data
  const availableRegions = useMemo(() =>
    REGION_ORDER.filter(r => regionCounts[r] > 0),
  [regionCounts]);

  // Apply region filter + search
  const filtered = useMemo(() => {
    let list = deduped;
    if (regionFilter !== 'Todos') {
      list = list.filter(l => (l.region || 'Outros') === regionFilter);
    }
    if (search.trim()) {
      const term = search.toLowerCase();
      list = list.filter(l =>
        (l.groupName || '').toLowerCase().includes(term) ||
        (REGION_LABELS[l.region] || l.region || '').toLowerCase().includes(term)
      );
    }
    // Sort by region order, then alphabetically
    return [...list].sort((a, b) => {
      const ri = REGION_ORDER.indexOf(a.region || 'Outros');
      const rj = REGION_ORDER.indexOf(b.region || 'Outros');
      if (ri !== rj) return ri - rj;
      return (a.groupName || '').localeCompare(b.groupName || '', 'pt-BR');
    });
  }, [deduped, regionFilter, search]);

  const handleClick = (league) => {
    const season = league.years[0] ?? new Date().getFullYear();
    const compId = league.yearToId[season] || league.id || encodeURIComponent(league.groupName);
    navigate(`/natacao/competicao/${compId}/${season}`);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-1">
            <img src="/vukz-logo.png" alt="VUKZ" className="h-8 w-auto"
              onError={(e) => { e.target.style.display = 'none'; }} />
            <h1 className="text-3xl font-black tracking-tight" style={{ color: VUKZ.text }}>
              {t.title}
            </h1>
          </div>
          <p className="text-base font-medium text-gray-500">
            {t.subtitle}
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-5">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.searchPlaceholder}
            className="w-full pl-12 pr-5 py-3 rounded-xl border text-sm font-medium focus:outline-none bg-white"
            style={{ borderColor: VUKZ.border, color: VUKZ.text }}
          />
        </div>

        {/* Filter Buttons */}
        {!loading && !error && (
          <div className="flex flex-wrap gap-2 mb-7">
            <FilterBtn
              label={t.filterAll}
              count={regionCounts['Todos'] || 0}
              active={regionFilter === 'Todos'}
              onClick={() => setRegionFilter('Todos')}
            />
            {availableRegions.map(r => (
              <FilterBtn
                key={r}
                label={REGION_LABELS[r] || r}
                count={regionCounts[r] || 0}
                active={regionFilter === r}
                onClick={() => setRegionFilter(r)}
              />
            ))}
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div>
            <div className="flex gap-2 mb-7">
              {[1,2,3,4,5].map(i => <div key={i} className="h-9 w-24 bg-gray-200 rounded-xl animate-pulse" />)}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {[1,2,3,4,5,6].map(i => <div key={i} className="h-24 rounded-2xl bg-gray-100 animate-pulse" />)}
            </div>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="bg-white rounded-2xl border p-12 text-center" style={{ borderColor: VUKZ.border }}>
            <p className="text-base font-bold text-red-600">{error}</p>
          </div>
        )}

        {/* Grid */}
        {!loading && !error && (
          filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border p-12 text-center" style={{ borderColor: VUKZ.border }}>
              <p className="text-base font-bold text-gray-400">{t.noResults}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map((league, idx) => (
                <CompetitionCard key={`${league.groupName}-${idx}`} league={league} onClick={() => handleClick(league)} />
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default SwimmingChampionshipsPage;
